import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import type { ReactNode } from 'react';
import {
    Box,
    Flex,
    Text,
} from '@chakra-ui/react';
import { AppDialog, DialogButton } from '../common/AppDialog';
import { Plug, PlugZap } from 'lucide-react';
import {
    startMcpBridge,
    stopMcpBridge,
    useMcpApprovalStore,
} from '../../services/mcp/mcpBridgeClient';
import { useMcpBridgeStore, type McpConnectionState } from '../../services/mcp/useMcpBridgeStore';
import { bridgeBaseUrl } from '../../services/mcp/mcpBridgeProtocol';

/**
 * MCP Bridge Host
 *
 * Mounts once at app root: starts/stops the bridge client with the
 * persisted enable flag and renders the per-request approval dialog when
 * the security gate is on.
 */
export function McpBridgeHost() {
    const { t } = useTranslation();
    const enabled = useMcpBridgeStore((s) => s.enabled);
    const pending = useMcpApprovalStore((s) => s.pending);
    const resolve = useMcpApprovalStore((s) => s.resolve);

    useEffect(() => {
        if (enabled) startMcpBridge();
        return () => stopMcpBridge();
    }, [enabled]);

    const current = pending[0];

    return (
        <AppDialog
            isOpen={Boolean(current)}
            onClose={() => {
                if (current) resolve(current.id, false);
            }}
            size="md"
            title={t('misc.mCPAgentRequest')}
            showCloseButton={false}
            footer={
                <>
                    <DialogButton
                        variant="secondary"
                        onClick={() => {
                            if (current) resolve(current.id, false);
                        }}
                    >{t('misc.deny')}</DialogButton>
                    <DialogButton
                        variant="primary"
                        onClick={() => {
                            if (current) resolve(current.id, true);
                        }}
                    >{t('misc.allow')}</DialogButton>
                </>
            }
        >
            {current && (
                <VStackLike>
                    <Text fontSize="sm" color="var(--color-text-secondary)">{t('misc.anExternalAIAgentWantsToRunAToolAllowThisRequest')}</Text>
                    <Box
                        w="100%"
                        px={3}
                        py={2}
                        borderRadius="var(--cad-radius-md)"
                        bg="var(--color-bg-tertiary)"
                        borderWidth="1px"
                        borderColor="var(--color-border)"
                    >
                        <Text fontFamily="mono" fontSize="sm" fontWeight="semibold">
                            {current.tool}
                        </Text>
                        <Text fontFamily="mono" fontSize="xs" color="var(--color-text-secondary)" wordBreak="break-word">
                            {current.summary}
                        </Text>
                    </Box>
                    {pending.length > 1 && (
                        <Text fontSize="xs" color="var(--color-text-muted)">
                            {pending.length - 1} more request(s) queued.
                        </Text>
                    )}
                </VStackLike>
            )}
        </AppDialog>
    );
}

/** Tiny vertical stack without importing the full VStack name into scope. */
function VStackLike({ children }: { children: ReactNode }) {
    return (
        <Box display="flex" flexDir="column" gap={3} alignItems="start">
            {children}
        </Box>
    );
}

/**
 * Status badge for the StatusBar: shows MCP bridge state and tool count.
 * Renders in the disabled state too — a hidden badge hides exactly the reason
 * an external agent cannot see any tools.
 */
const STATE_LABEL: Record<McpConnectionState, { text: string; color: string }> = {
    disabled: { text: 'MCP off', color: 'gray' },
    connecting: { text: 'MCP…', color: 'yellow' },
    connected: { text: 'MCP live', color: 'green' },
    offline: { text: 'MCP down', color: 'red' },
};

export function McpStatusBadge() {
    const connection = useMcpBridgeStore((s) => s.connection);
    const port = useMcpBridgeStore((s) => s.port);
    const servedCount = useMcpBridgeStore((s) =>s.servedCount); const state = STATE_LABEL[connection]; const Icon = connection === 'connected' ? PlugZap : Plug; return (<Flex align="center" gap={1.5} title={`${state.text} · ${bridgeBaseUrl(port)} · ${servedCount} request(s) served`}>
            <Box as="span" display="inline-flex" alignItems="center" color="icon.base" cursor="default" aria-label={state.text}>
                <Icon size={13} />
            </Box>
        </Flex>
    );
}
